import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";
import { Icon } from "@/components/Icon/Icon";

import { AlternativePhrase } from "../../../schema/copywritingSettings";

const emptyPhrase: AlternativePhrase = { original: "", replacement: "" };

interface AlternativePhrasesSectionProps {
	value: AlternativePhrase[];
	onChange: (value: AlternativePhrase[]) => void;
}

export const AlternativePhrasesSection: FunctionComponent<AlternativePhrasesSectionProps> = ({
	value,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`copywritingSettings.alternativePhrases.${key}`);

	const updateEntry = (index: number, field: keyof AlternativePhrase, val: string) => {
		const updated = [...value];

		updated[index] = { ...updated[index], [field]: val };
		onChange(updated);
	};

	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm text-text-meta">{ts("description")}</p>

			{value.length > 0 && (
				<div className="flex items-center gap-3 text-xs font-medium text-text-meta uppercase tracking-wide">
					<div className="flex-1">{ts("originalLabel")}</div>
					<div className="w-4" />
					<div className="flex-1">{ts("replacementLabel")}</div>
					<div className="w-8" />
				</div>
			)}

			{value.map((entry, index) => (
				<div key={index} className="flex items-start gap-3">
					<div className="flex-1">
						<InputField
							handleChange={(e) => updateEntry(index, "original", e.target.value)}
							name={`original-${index}`}
							placeholder={ts("originalPlaceholder")}
							showError={false}
							value={entry.original}
						/>
					</div>
					<span className="mt-2 text-text-meta text-sm">→</span>
					<div className="flex-1">
						<InputField
							handleChange={(e) => updateEntry(index, "replacement", e.target.value)}
							name={`replacement-${index}`}
							placeholder={ts("replacementPlaceholder")}
							showError={false}
							value={entry.replacement}
						/>
					</div>
					<button
						className="mt-2 text-text-meta hover:text-status-error"
						type="button"
						onClick={() => onChange(value.filter((_, i) => i !== index))}
					>
						<Icon className="w-4 h-4" icon={faTrash} />
					</button>
				</div>
			))}

			<button
				className="flex items-center gap-1 text-sm text-blue-action hover:underline self-start"
				type="button"
				onClick={() => onChange([...value, { ...emptyPhrase }])}
			>
				<Icon className="w-3 h-3" icon={faPlus} />
				{ts("addRow")}
			</button>
		</div>
	);
};
